// MenuUsuario.js — Header con sesión iniciada (modo `login_icono`, p. ej. Learning).
//
//  · EnlacesSesion: accesos junto al logo (por defecto «Progreso» y «Mis certificados»).
//  · BotonUsuario: el avatar dentro de la cápsula de vidrio (al lado de la lupa)
//    que despliega el menú de la cuenta: cabecera con el usuario y su progreso,
//    los accesos configurados y «Cerrar sesión». Para administradores agrega
//    «Panel de administración».
//
// Todo es editable en la sección header: `enlaces_sesion` y `menu_usuario`
// (listas de {texto, url, icono}). Sin ellas se usan los valores por defecto.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { mentor2Service } from '../../services/api';
import useMiPlan from '../../hooks/useMiPlan';

export const ENLACES_SESION_DEF = [
  { texto: 'Home', url: '/', icono: 'inicio' },
  { texto: 'Mis certificados', url: '/certificados', icono: 'certificado' },
  { texto: 'Progreso', url: '/contenidos', icono: 'progreso' },
];
export const MENU_USUARIO_DEF = [
  { texto: 'Progreso', url: '/contenidos', icono: 'progreso' },
  { texto: 'Administrar perfil', url: '/perfil', icono: 'perfil' },
  { texto: 'Tablero', url: '/progress', icono: 'tablero' },
  { texto: 'Todos los cursos', url: '/', icono: 'cursos' },
  { texto: 'Mis certificados', url: '/certificados', icono: 'certificado' },
];

// ---------- Iconos (trazo, heredan color) ----------
const P = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' };
export const ICONOS = {
  inicio: <svg viewBox="0 0 24 24" {...P}><path d="M3.5 11 12 4l8.5 7" /><path d="M5.5 9.5V20h13V9.5" /><path d="M10 20v-5.5h4V20" /></svg>,
  progreso: <svg viewBox="0 0 24 24" {...P}><path d="M4 19V5" /><path d="M4 19h16" /><path d="m7.5 14.5 3.5-4 3 3 5-6" /></svg>,
  certificado: <svg viewBox="0 0 24 24" {...P}><circle cx="12" cy="9" r="5" /><path d="m9.5 13.3-1.5 7.2 4-2.2 4 2.2-1.5-7.2" /><path d="m10.2 9 1.3 1.3 2.4-2.6" /></svg>,
  perfil: <svg viewBox="0 0 24 24" {...P}><circle cx="12" cy="8" r="4" /><path d="M4.5 20.5c.9-3.6 3.9-6 7.5-6s6.6 2.4 7.5 6" /></svg>,
  tablero: <svg viewBox="0 0 24 24" {...P}><rect x="3.5" y="3.5" width="7" height="8" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="5" rx="1.5" /><rect x="13.5" y="11.5" width="7" height="9" rx="1.5" /><rect x="3.5" y="14.5" width="7" height="6" rx="1.5" /></svg>,
  cursos: <svg viewBox="0 0 24 24" {...P}><rect x="3" y="5" width="18" height="12" rx="2" /><path d="m10.5 8.8 4 2.2-4 2.2Z" /><path d="M8 20.5h8" /></svg>,
  admin: <svg viewBox="0 0 24 24" {...P}><path d="M12 3 4.5 6v5.5c0 4.4 3.1 8.1 7.5 9.5 4.4-1.4 7.5-5.1 7.5-9.5V6L12 3Z" /><circle cx="12" cy="11" r="2.5" /></svg>,
  salir: <svg viewBox="0 0 24 24" {...P}><path d="M15 4h3.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H15" /><path d="M10 16.5 14.5 12 10 7.5" /><path d="M14.5 12H4" /></svg>,
  plan: <svg viewBox="0 0 24 24" {...P}><path d="M5 20.5V5.5a2 2 0 0 1 2-2h10.5v14H7a2 2 0 0 0-2 2Z" /><path d="M5 19.5a1.5 1.5 0 0 0 1.5 1.5h11" /><path d="m9 9.5 2 2 4-4" /></svg>,
  enlace: <svg viewBox="0 0 24 24" {...P}><path d="M10 14 20 4" /><path d="M14 4h6v6" /><path d="M19 13.5v5A1.5 1.5 0 0 1 17.5 20h-12A1.5 1.5 0 0 1 4 18.5v-12A1.5 1.5 0 0 1 5.5 5h5" /></svg>,
};
const ico = (k) => ICONOS[k] || ICONOS.enlace;
// Alumnos con plan de formación (subrol especial): «Mi plan de formación» después de Home.
const ENLACE_PLAN = { texto: 'Mi plan de formación', url: '/mi-plan', icono: 'plan' };
const conPlan = (lista, modo) => {
  if (modo !== 'plan' || lista.some(e => e.url === ENLACE_PLAN.url)) return lista;
  const i = lista.findIndex(e => e.url === '/');
  return [...lista.slice(0, i + 1), ENLACE_PLAN, ...lista.slice(i + 1)];
};
const esExterno = (u) => /^https?:\/\//i.test(u || '');

// ---------- Enlaces junto al logo ----------
const Enlaces = styled.nav`
  display: flex; align-items: center; gap: .25rem; margin-left: 1.6rem;
  a {
    position: relative; display: inline-flex; align-items: center; gap: .45rem; height: 40px; padding: 0 .75rem;
    border-radius: 999px; color: ${p => p.$color}; text-decoration: none; font-size: .92rem; font-weight: 600; white-space: nowrap;
    opacity: .82; transition: opacity .2s, background .2s;
    svg { width: 18px; height: 18px; flex: none; }
    &::after {
      content: ''; position: absolute; left: .75rem; right: .75rem; bottom: 4px; height: 2px; border-radius: 1px;
      background: ${p => p.$acento}; transform: scaleX(0); transform-origin: left; transition: transform .28s cubic-bezier(.2,.8,.2,1);
    }
    &:hover { opacity: 1; background: rgba(255,255,255,.07); }
    &:hover::after, &[aria-current='page']::after { transform: scaleX(1); }
    &[aria-current='page'] { opacity: 1; }
    &:focus-visible { outline: 2px solid ${p => p.$acento}; outline-offset: 2px; opacity: 1; }
  }
  @media (max-width: 900px) { margin-left: .6rem; a span { display: none; } a { padding: 0 .6rem; } a::after { left: .5rem; right: .5rem; } }
  @media (max-width: 600px) { display: none; }
  @media (prefers-reduced-motion: reduce) { a::after { transition: none; } }
`;

export const EnlacesSesion = ({ enlaces, color, acento }) => {
  const { pathname } = useLocation();
  const { modo } = useMiPlan();
  const lista = conPlan(Array.isArray(enlaces) && enlaces.length ? enlaces : ENLACES_SESION_DEF, modo);
  return (
    <Enlaces $color={color} $acento={acento} aria-label="Accesos de tu cuenta">
      {lista.filter(e => e.texto && e.url).map((e, i) => (
        esExterno(e.url)
          ? <a key={i} href={e.url} target="_blank" rel="noopener noreferrer">{ico(e.icono)}<span>{e.texto}</span></a>
          : <Link key={i} to={e.url} aria-current={pathname === e.url ? 'page' : undefined}>{ico(e.icono)}<span>{e.texto}</span></Link>
      ))}
    </Enlaces>
  );
};

// ---------- Botón de usuario + menú ----------
const Caja = styled.div`position: relative;`;
const Boton = styled.button`
  display: flex; align-items: center; gap: 4px; height: 44px; padding: 0 8px 0 3px; border: 0; border-radius: 999px;
  background: transparent; color: ${p => p.$color}; cursor: pointer; font: inherit;
  transition: background .2s;
  .cara {
    display: grid; place-items: center; width: 38px; height: 38px; border-radius: 50%; overflow: hidden; flex: none;
    background: ${p => p.$acento}; color: #fff; font-size: .9rem; font-weight: 800; letter-spacing: .02em;
    box-shadow: 0 0 0 2px rgba(255,255,255,.18);
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .flecha { width: 16px; height: 16px; transition: transform .25s ease; }
  &[aria-expanded='true'] .flecha { transform: rotate(180deg); }
  &:hover, &[aria-expanded='true'] { background: rgba(255,255,255,.12); }
  &:focus-visible { outline: 2px solid ${p => p.$acento}; outline-offset: 3px; }
`;
const abrir = keyframes`from { opacity: 0; transform: translateY(-6px) scale(.97); } to { opacity: 1; transform: none; }`;
const Menu = styled.div`
  position: absolute; top: calc(100% + 12px); right: -6px; z-index: 3200; width: min(320px, calc(100vw - 24px));
  border-radius: 16px; overflow: hidden; transform-origin: top right;
  background: rgba(16,18,22,.94); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 24px 60px -18px rgba(0,0,0,.9), inset 0 0 0 1px rgba(255,255,255,.1);
  color: #eef1f5; animation: ${abrir} .18s cubic-bezier(.2,.8,.2,1);
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Cabecera = styled(Link)`
  display: block; padding: 1rem 1.05rem .95rem; text-decoration: none; color: inherit;
  background: linear-gradient(160deg, color-mix(in srgb, ${p => p.$acento} 30%, transparent), transparent 75%);
  .quien { display: flex; align-items: center; gap: .75rem; }
  .cara { display: grid; place-items: center; width: 44px; height: 44px; border-radius: 50%; overflow: hidden; flex: none;
    background: ${p => p.$acento}; color: #fff; font-weight: 800; }
  .cara img { width: 100%; height: 100%; object-fit: cover; }
  strong { display: block; font-size: .98rem; font-weight: 700; line-height: 1.2; }
  small { display: block; margin-top: 2px; font-size: .78rem; color: #aab2bf; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 210px; }
  .avance { margin-top: .85rem; }
  .avance .linea { display: flex; justify-content: space-between; font-size: .78rem; color: #c8ced8; margin-bottom: .4rem; }
  .avance .linea b { color: #fff; font-weight: 700; }
  .barra { height: 6px; border-radius: 3px; background: rgba(255,255,255,.14); overflow: hidden; }
  .barra i { display: block; height: 100%; border-radius: 3px; background: ${p => p.$acento};
    box-shadow: 0 0 10px color-mix(in srgb, ${p => p.$acento} 70%, transparent); transition: width .8s cubic-bezier(.2,.8,.2,1); }
  &:hover .linea span:last-child { color: #fff; }
  &:focus-visible { outline: 2px solid ${p => p.$acento}; outline-offset: -2px; }
`;
const Lista = styled.div`
  padding: .4rem;
  a, button {
    width: 100%; display: flex; align-items: center; gap: .75rem; padding: .62rem .7rem; border: 0; border-radius: 10px;
    background: none; color: inherit; font: inherit; font-size: .92rem; font-weight: 500; text-align: left; text-decoration: none; cursor: pointer;
    transition: background .15s;
    svg { width: 19px; height: 19px; flex: none; color: #9aa3b1; transition: color .15s; }
    &:hover, &:focus-visible { background: rgba(255,255,255,.08); outline: none; }
    &:hover svg, &:focus-visible svg { color: ${p => p.$acento}; }
  }
  .divisor { height: 1px; margin: .35rem .5rem; background: rgba(255,255,255,.08); }
  .salir { color: #ffb4b4; }
  .salir svg { color: #ff8a8a; }
`;

export const BotonUsuario = ({ user, items, color, acento, proyecto, onLogout, textoProgreso }) => {
  const [abierto, setAbierto] = useState(false);
  const [avance, setAvance] = useState(null);   // { pct, empezados }
  const caja = useRef(null);
  const navigate = useNavigate();
  const { modo } = useMiPlan();
  const lista = conPlan(Array.isArray(items) && items.length ? items : MENU_USUARIO_DEF, modo);
  const nombre = user?.nombre || user?.name || 'Tu cuenta';
  const iniciales = nombre.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  const foto = user?.avatar_url ? `/backend/${user.avatar_url}` : null;

  // Cursos del proyecto: los del catálogo de la portada (o todos los documentos).
  const docIds = useMemo(() => {
    const cat = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'catalogo_streaming')?.config;
    const ids = (cat?.items || []).map(it => it.documento_id).filter(Boolean);
    return ids.length ? ids : (proyecto?.documentos || []).map(d => d.id);
  }, [proyecto]);

  // El avance se pide al abrir el menú (no en cada página).
  useEffect(() => {
    if (!abierto || avance || !docIds.length) return;
    mentor2Service.progresoProyecto(docIds).then(res => {
      const prog = res.data?.progreso || {}; const lec = res.data?.lecciones || {};
      let vistas = 0; let total = 0; let empezados = 0;
      docIds.forEach(id => {
        if (Number(prog[String(id)] || 0) > 0) { empezados += 1; vistas += lec[String(id)]?.hechas || 0; total += lec[String(id)]?.total || 0; }
      });
      setAvance({ pct: total ? Math.round((vistas / total) * 100) : 0, empezados, vistas });
    }).catch(() => setAvance({ pct: 0, empezados: 0, vistas: 0 }));
  }, [abierto, avance, docIds]);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    const tecla = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    window.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('mousedown', fuera); window.removeEventListener('keydown', tecla); };
  }, [abierto]);

  const ir = (url) => { setAbierto(false); if (esExterno(url)) window.open(url, '_blank', 'noopener'); else navigate(url); };

  return (
    <Caja ref={caja}>
      <Boton type="button" $color={color} $acento={acento} aria-haspopup="menu" aria-expanded={abierto}
        aria-label={`Tu cuenta: ${nombre}`} onClick={() => setAbierto(a => !a)}>
        <span className="cara">{foto ? <img src={foto} alt="" /> : iniciales}</span>
        <svg className="flecha" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </Boton>
      {abierto && (
        <Menu role="menu" aria-label="Tu cuenta">
          <Cabecera to="/contenidos" $acento={acento} onClick={() => setAbierto(false)} role="menuitem">
            <span className="quien">
              <span className="cara">{foto ? <img src={foto} alt="" /> : iniciales}</span>
              <span><strong>{nombre}</strong>{user?.email && <small>{user.email}</small>}</span>
            </span>
            <span className="avance" style={{ display: 'block' }}>
              <span className="linea">
                <span>{textoProgreso || 'Tu progreso'}{avance ? <> · <b>{avance.pct} %</b></> : ''}</span>
                <span>{avance ? (avance.empezados ? `${avance.empezados} ${avance.empezados === 1 ? 'curso' : 'cursos'} en marcha` : 'Empieza un curso') : 'Cargando…'}</span>
              </span>
              <span className="barra" style={{ display: 'block' }}><i style={{ width: `${avance?.pct || 0}%` }} /></span>
            </span>
          </Cabecera>
          <Lista $acento={acento}>
            {user?.role === 'admin' && (
              <button type="button" role="menuitem" onClick={() => ir('/admin-panel')}>{ICONOS.admin}Panel de administración</button>
            )}
            {lista.filter(e => e.texto && e.url).map((e, i) => (
              <button key={i} type="button" role="menuitem" onClick={() => ir(e.url)}>{ico(e.icono)}{e.texto}</button>
            ))}
            <div className="divisor" />
            <button type="button" role="menuitem" className="salir" onClick={() => { setAbierto(false); onLogout(); }}>{ICONOS.salir}Cerrar sesión</button>
          </Lista>
        </Menu>
      )}
    </Caja>
  );
};
